import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AxiosError } from 'axios';
import { Location } from 'history';
import moment from "moment";
import client from 'lib/CommonApi/client';
import stringUtils from 'common/stringUtils';
import { callAlertWeb, callNativeWeb, footerShowEventWeb, hideProgressWeb, showProgressWeb } from 'common/appInterface';
import ApprovalDetailPortal from './ApprovalDetailPortal';

let nextPage = 2;
const ApprovalListPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const screenId = "TRANS02";
  const screenName = t("TRAN02.title");

  const [approvalEndDate, setApprovalEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [approvalStartDate, setApprovalStartDate] = useState(moment().add(-1, "months").format("YYYY-MM-DD"));
  
  const [key, setKey] = useState("1");
  const [approvalList, setApprovalList] = useState<ApprovalInfo[]>([]);
  const [hasMore, setHasMore] = useState(false);

  const [approvalDetailProps, setApprovalDetailProps] = useState<ApprovalDetailProps>({ tradeApprovalSeq: "", tradeDate: "", onClose: () => setDetail(false) });
  const [isDetail, setDetail] = useState(false);

  /** 뒤로가기 처리 (backKeyEvent_App (APP > WEB)) */
  const callbackBackkeyEvent = useCallback(() => {
    if (isDetail) {
      setDetail(false);
    }
    else {
      navigate(-1);
    }
  }, [isDetail, navigate]);

  /** 승인내역검색 */
  const callApprovalFirstSearch = useCallback(async (approvalStartDate: string, approvalEndDate: string) => {
    nextPage = 2;
    if (key === "4") {
      if (approvalEndDate < approvalStartDate) {
        return;
      }
    }

    setApprovalStartDate(approvalStartDate);
    setApprovalEndDate(approvalEndDate);

    showProgressWeb();
    await client.get("/api/app/approval/selectApprovalList", { params: { ...location.state.query, page : 1, alternativeCardNo: location.state.info.alternativeCardNo, approvalStartDate : approvalStartDate, approvalEndDate : approvalEndDate }}).then((response) => {
      let result = response.data;
      if(result.response.length !== 0){
        setApprovalList(result.response);
        if(result.response[0].rowsPerPage < Number.parseInt(result.response[0].totalCount)){
          setHasMore(true);
        }else{
          setHasMore(false);
        }
      } else {
        setApprovalList(result.response);
      }
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
    });
  }, [key, location]);

  /** 승인내역검색 Next */
  const callApprovalNextSearch = useCallback(async () => {
    if(!hasMore){
      return;
    }

    showProgressWeb();
    await client.get("/api/app/approval/selectApprovalList", { params: { ...location.state.query, page :nextPage, alternativeCardNo: location.state.info.alternativeCardNo, approvalStartDate : approvalStartDate, approvalEndDate : approvalEndDate }}).then((response) => {
      let fetchData = response.data.response;
      const mergedData = approvalList.concat(...fetchData);
      setHasMore(mergedData.length !== Number.parseInt(fetchData[0].totalCount) ? true : false);
      setApprovalList(mergedData);
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
      nextPage++;
    });
  }, [approvalEndDate, approvalList, approvalStartDate, hasMore, location]);

  /** 1개월 검색 */
  const oneMonthSearch = useCallback(() => {
    setKey("1");

    let startDate = moment().add(-1, "months").format("YYYY-MM-DD");
    let endDate = moment().format("YYYY-MM-DD");
    callApprovalFirstSearch(startDate, endDate);
  }, [callApprovalFirstSearch]);

  /** 3개월 검색 */
  const threeMonthSearch = useCallback(() => {
    setKey("2");

    let startDate = moment().add(-3, "months").format("YYYY-MM-DD");
    let endDate = moment().format("YYYY-MM-DD");
    callApprovalFirstSearch(startDate, endDate);
  }, [callApprovalFirstSearch]);

  /** 12개월 검색 */
  const oneYearSearch = useCallback(() => {
    setKey("3");

    let startDate = moment().add(-12, "months").format("YYYY-MM-DD");
    let endDate = moment().format("YYYY-MM-DD");
    callApprovalFirstSearch(startDate, endDate);
  }, [callApprovalFirstSearch]);

  /** 기간조회 */
  const termSearch = useCallback(() => {
    setKey("4");

    setApprovalList([]);
    setApprovalStartDate(moment().add(-1, "months").format("YYYY-MM-DD"));
    setApprovalEndDate(moment().format("YYYY-MM-DD"));
  }, []);

  useEffect(() => {
    // setDetail(true);
  }, [approvalDetailProps]);

  /** 승인내역 상세 */
  const clickDetail = useCallback((tradeApprovalSeq: string, tradeDate: string) => {
    setApprovalDetailProps((value) => ({ ...value, tradeApprovalSeq: tradeApprovalSeq, tradeDate: tradeDate }));
    setDetail(true);
  }, []);

  /** DatePicker 읽기전용 처리 */
  const onDatePickerRef = ((el: any) => {
    if (el && el.input) {
      el.input.readOnly = true;
    }
  })

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, screenName);
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);

    oneMonthSearch();

    // BODY클래스 추가
    document.body.classList.add("bgColorTy01");

    return () => {
      // BODY클래스 제거
      document.body.classList.remove("bgColorTy01");
    }

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 뒤로가기 이벤트 리스너 (backKeyEvent_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  return (
    <div id="wrap">
      <div id="container" className="pcHidden mycardhistory bgColorTy01">
        <div className="inner">
          <div className="optionWrap">
            {/*<ul className={key ==="4" ? "optionList setTerm on" : "optionList setTerm"}>*/}
            <ul className={"optionList setTerm on"}>
              <li className="option">
                <input type="radio" id="opt01" name="opt" checked={key === "1" ? true : false} readOnly />
                <label htmlFor="opt01">
                  <span onClick={oneMonthSearch}>{t("TRAN02.button01")}</span>
                </label>
              </li>
              <li className="option">
                <input type="radio" id="opt02" name="opt" checked={key === "2" ? true : false} readOnly />
                <label htmlFor="opt02">
                  <span onClick={threeMonthSearch}>{t("TRAN02.button02")}</span>
                </label>
              </li>
              <li className="option">
                <input type="radio" id="opt03" name="opt" checked={key === "3" ? true : false} readOnly />
                <label htmlFor="opt03">
                  <span onClick={oneYearSearch}>{t("TRAN02.button03")}</span>
                </label>
              </li>
              <li className="option set">
                <input type="radio" id="opt04" name="opt" checked={key === "4" ? true : false} readOnly />
                <label htmlFor="opt04">
                  <span onClick={termSearch}>{t("TRAN02.button04")}</span>
                </label>
              </li>
            </ul>
            <div className="setDetail">
              <div className="calenderWrap wide">
                <div className="controller">
                  <div className="inputBoxTyAD01">
                    <DatePicker onChange={(date) => setApprovalStartDate(stringUtils.dateFormat(date,'yyyy-MM-dd'))} dateFormat="yyyy.MM.dd" selected={moment(approvalStartDate).toDate()} maxDate={moment(approvalEndDate).toDate()} showPopperArrow={false} popperModifiers={[{ name: "preventOverflow", options: { altAxis: true }}]} dateFormatCalendar={"yyyy.MM"} ref={onDatePickerRef} readOnly={key !=="4"} ></DatePicker>
                  </div>
                  <span>~</span>
                  <div className="inputBoxTyAD01">
                    <DatePicker onChange={(date) => setApprovalEndDate(stringUtils.dateFormat(date,'yyyy-MM-dd'))} dateFormat="yyyy.MM.dd" selected={moment(approvalEndDate).toDate()} minDate={moment(approvalStartDate).toDate()} showPopperArrow={false} popperModifiers={[{ name: "preventOverflow", options: { altAxis: true }}]} dateFormatCalendar={"yyyy.MM"} ref={onDatePickerRef} readOnly={key !=="4"}></DatePicker>
                  </div>
                </div>
              </div>
              {key ==="4" ? <p className="msg">{t("TRAN02.explan01")}</p> : <p></p>}
              {key ==="4" ? <button type="button" className="btnPrimaryTy01 btnsm" onClick={() => callApprovalFirstSearch(approvalStartDate, approvalEndDate)}><span >{t("TRAN02.button05")}</span></button> : <p></p>}
            </div>
          </div>
        </div>
        <section className="contents myPageSetting pb64-mb">
          <div className="inner">
            <div className="settingListWrap clfix">
              <dl className="settingList">
                <InfiniteScroll className="settingList" dataLength={approvalList.length} next={callApprovalNextSearch} hasMore={hasMore} loader={""}>
                  {approvalList.map((item, index) => 
                    <React.Fragment key ={index}>
                      {(index === 0 || approvalList[index -1].tradeDate !== approvalList[index].tradeDate) && <dt><span className="day">{stringUtils.dateFormatDefault(item.tradeDate)}</span></dt>}
                      <dd>
                        <button type="button" onClick={() => clickDetail(item.tradeApprovalSeq, item.tradeDate)}>
                          <span className="spot">{item.merchantName}</span>
                          <p className="date">{stringUtils.dateTimeFormat(item.approvalTime)} {t(`TRAN02.auth.${item.salesKindDivisionCode}`)}</p>
                          <span className={item.salesKindDivisionCode === '05' ? "place out" : "place"}>{item.salesKindDivisionCode === '05' ? stringUtils.moneyFormat(item.cardApprovalAmount) : '+' + stringUtils.moneyFormat(item.cardApprovalAmount)} KRW</span>
                        </button>
                      </dd>
                    </React.Fragment>
                  )}
                </InfiniteScroll>
                {approvalList.length === 0 &&
                  <dt><span className="day">{t("TRAN02.explan02")}</span></dt>
                }
              </dl>
            </div>
          </div>
        </section>
      </div>
      {isDetail &&
        <ApprovalDetailPortal { ...approvalDetailProps }></ApprovalDetailPortal>
      }
    </div>
  );
};

export default ApprovalListPage;